import React, { useMemo } from "react";
import { Button, Icon } from "@shared/components";
import { changeListeners, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { Project, WorkType } from "@shared/models";
import { CreateProjectDto, ProjectFormShape } from "@containers/Project/interfaces";

import {
  getInitValues,
  validationSchema,
  prepareSubmitValue,
  PROJECT_TYPES_OPTIONS,
  handlers,
  PROJECT_STATUS_OPTIONS,
} from "./formHelpers";
import { WorkTypeErrorMessage } from "./components";

import "./index.scss";

interface ProjectFormProps {
  project: Project | null;
  workTypes: WorkType[];
  onDelete: () => void;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateProjectDto) => void;
}

interface ProjectFormFieldsProps extends ProjectFormProps {
  formValues: ProjectFormShape;
}

const ProjectFormFields: React.FunctionComponent<ProjectFormFieldsProps> = (props) => {
  const { project, formValues, onChangeForm, onCloseForm, onDelete, onSubmitForm, workTypes } = props;

  const workTypeOptions = useMemo(() => workTypes.map((i) => ({ label: i.name, value: i.id })), [workTypes]);

  const form = useAppForm({
    defaultValues: formValues,
    validators: schemaValidators(validationSchema),
    listeners: changeListeners(onChangeForm),
    onSubmit: ({ value }) => {
      onSubmitForm(prepareSubmitValue(validationSchema.parse(value)));
    },
  });

  return (
    <div className="project-form">
      <form onSubmit={formSubmitHandler(form)}>
        <div className="project-form-content">
          <div>
            <form.AppField name="name">
              {(field) => <field.Input type="text" labelTitle="Name*" placeholder="Name" />}
            </form.AppField>
          </div>
          <div>
            <form.AppField name="start_date">
              {(field) => <field.Input type="date" labelTitle="Start Date*" placeholder="Start Date" />}
            </form.AppField>
          </div>
          <div>
            <form.AppField name="code">
              {(field) => <field.Input type="text" labelTitle="Code*" placeholder="Code" />}
            </form.AppField>
          </div>
          <div>
            <form.AppField name="type">
              {(field) => (
                <field.Select searchable={false} data={PROJECT_TYPES_OPTIONS} labelTitle="Type*" placeholder="Type" />
              )}
            </form.AppField>
          </div>
          <div className="average-cost-wrapper">
            <form.AppField name="average_cost">
              {(field) => (
                <field.Input type="number" labelTitle="Average cost" placeholder="Average cost" additionalSymbol="$" />
              )}
            </form.AppField>
          </div>
          <div>
            <form.AppField name="manager">
              {(field) => (
                <field.AsyncSelect
                  labelTitle="Manager*"
                  className="manager-input"
                  placeholder="Select Manager"
                  getData={handlers.user.getData}
                  selectData={handlers.user.selectData}
                  prepareOptionFunction={handlers.user.prepareOptionFunction}
                  loadingAction={handlers.user.loadingAction}
                />
              )}
            </form.AppField>
          </div>
          <div>
            <form.AppField name="status">
              {(field) => (
                <field.Select searchable={false} cleanable={false} data={PROJECT_STATUS_OPTIONS} labelTitle="Status" />
              )}
            </form.AppField>
          </div>
          <div className="work-types-line">
            <div className="work-types-header">Work Types</div>
            <form.AppField name="work_types" mode="array">
              {(workTypesField) => (
                <div className="work-types-content">
                  <div className="work-types-content-items">
                    <div>
                      {workTypesField.state.value.map((_, i) => (
                        <div className="work-type-row" key={`${i}`}>
                          <div className="work-type">
                            <form.AppField name={`work_types[${i}].work_type_id`}>
                              {(field) => (
                                <field.Select
                                  searchable={false}
                                  data={workTypeOptions}
                                  labelTitle="Work Type*"
                                  placeholder="Work Type"
                                />
                              )}
                            </form.AppField>
                          </div>
                          <div className="hours">
                            <form.AppField name={`work_types[${i}].hours`}>
                              {(field) => (
                                <field.Input
                                  type="number"
                                  labelClassName="work-type-label"
                                  labelTitle="Hours*"
                                  placeholder="Work Type"
                                  min={0}
                                />
                              )}
                            </form.AppField>
                          </div>
                          <div className="actions">
                            <Icon onClick={() => workTypesField.removeValue(i)} type="clear" label="Remove work type" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="work-types-content-add-item">
                    <span onClick={() => workTypesField.pushValue({ work_type_id: "", hours: 0 })}>+ Add New</span>
                  </div>
                  <div className="add-work-types-error-row">
                    <form.Subscribe selector={(state) => state.submissionAttempts > 0}>
                      {(submitAttempted) => (
                        <WorkTypeErrorMessage meta={workTypesField.state.meta} submitAttempted={submitAttempted} />
                      )}
                    </form.Subscribe>
                  </div>
                </div>
              )}
            </form.AppField>
          </div>
        </div>

        <div className="project-form-footer">
          <div className="project-form-footer-left">
            {project ? (
              <Button type="button" className="clear padding" onClick={onDelete}>
                Delete
              </Button>
            ) : null}
          </div>

          <div className="project-form-footer-right">
            <Button type="button" className="close-btn margin-right" onClick={onCloseForm}>
              Cancel
            </Button>
            <Button type="submit" className="accent padding">
              {project ? "Save" : "Add Project"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

/** Keyed on the project's initial values: a project that arrives or changes while the form is open mounts a fresh form. */
const ProjectForm: React.FunctionComponent<ProjectFormProps> = (props) => {
  const formValues = useMemo(() => getInitValues(props.project), [props.project]);

  return <ProjectFormFields key={JSON.stringify(formValues)} formValues={formValues} {...props} />;
};

export default ProjectForm;
